import { useEffect, useMemo, useRef, useState } from 'react';

import { AdUnitReviewControls } from './AdUnitReviewControls';
import { AiActionButton } from './AiActionButton';

import { Skeleton } from '@/components/ui/skeleton';
import { CopyVariantCard } from '@/components/workbench/CopyVariantCard';
import { useConceptCopyVariants } from '@/hooks/useConceptCopyVariants';
import { useSelectConceptCopy } from '@/hooks/useSelectConceptCopy';
import { COPY_FIELDS } from '@/lib/concept-copy/platform-limits';
import type { ConceptCopyVariant } from '@/lib/concept-copy/types';
import {
  ITEM_META,
  SECTION_LABEL,
  SELECTABLE_PILL,
  SELECTABLE_PILL_OFF,
  SELECTABLE_PILL_ON,
} from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import type { ConceptCopySelectionBlockedCode } from '@/services/data/ConceptCopyVariantService';

/**
 * The Concept Workbench's `Content` section — the copy a marketer will actually
 * ship, and the one place the copy-variant layer is reachable.
 *
 * ── WHY AN OPTION STRIP AND NOT A FLAT LIST ───────────────────────────────
 * One `ad_units` row is one OPTION: a headline, a caption and a CTA written
 * together, in one voice, in one model call. Flattening N rows into one column
 * of 3N cards destroys the only thing that makes an option an option — you
 * would see nine lines with no way to tell which headline was written for which
 * caption, and "More like this" would have no coherent thing to anchor on.
 *
 * Stacking N labelled groups keeps the pairing but not the column: at three
 * options this section is nine cards deep inside a 300px rail that also has to
 * hold size variants, text, typography, position and dimensions. The layer
 * controls would fall below the fold on every concept that has copy.
 *
 * So the options are a RADIO GROUP and exactly one triplet renders. That is the
 * same mechanism the `Size variants` strip one section above already uses for
 * the same reason (N coherent alternatives, one shown), which keeps the column
 * speaking one language instead of two.
 *
 * ── NUMBERING IS BY CREATION, WITH AN ID TIEBREAK ─────────────────────────
 * Siblings are APPENDED, never a replacement, so `Option 1` must stay `Option 1`
 * when "More like this" adds two more. Ordering is ascending by `created_at` —
 * and then by id, because a generated SET lands in ONE insert and its rows share
 * a timestamp to the microsecond (verified on the live rows for concept
 * 64bec3c7…). Sorting on the timestamp alone leaves the order to the database
 * and the numbers would shuffle between refetches.
 */

export interface ConceptCopySectionProps {
  conceptId: string | null | undefined;
  /**
   * Enables the review controls. One copy option IS one `ad_units` row, and
   * `transition_ad_unit_review` is scoped by brief — without the brief there is
   * no cache to invalidate and no review to run, so the buttons do not render
   * rather than rendering disabled.
   */
  briefId?: string;
  /**
   * CONTROLLED/UNCONTROLLED HYBRID — the fix for W-1 (Preview silently always
   * showed "Option 1" regardless of what the user picked here).
   *
   * `ConceptDesignBody` is the parent of BOTH this section (via
   * `WorkbenchInspector`) and `ConceptPreviewPane`; it owns the id so the two
   * siblings agree. Passing `undefined` (the default — every other call site,
   * including this file's own tests) keeps this section's original
   * self-contained behaviour, so nothing already exercising it in isolation
   * broke. Passing `null` or an id makes the section controlled: it stops
   * tracking selection internally and defers to the parent, still reporting
   * every change through `onSelectIdChange` either way.
   */
  selectedId?: string | null;
  onSelectIdChange?: (id: string) => void;
  className?: string;
}

/** How many siblings one "More like this" asks for. */
const SIBLING_COUNT = 2;

interface Notice {
  text: string;
  tone: 'muted' | 'destructive';
}

function evidenceReceipt(lineage: ConceptCopyVariant['briefEvidenceLineage']): string | null {
  if (!lineage) return null;
  if (lineage.state === 'no_decision') {
    return 'No Brief decision was attached when this option was written.';
  }
  if (lineage.state === 'no_evidence') {
    return `Brief decision v${lineage.decision?.version ?? 'unknown'} was attached, but no evidence sources were linked.`;
  }
  const sourceLabel = `${lineage.sourceCount} linked ${lineage.sourceCount === 1 ? 'source' : 'sources'}`;
  return `Written with Brief decision v${lineage.decision?.version ?? 'unknown'} and ${sourceLabel}.${lineage.limitations.includes('performance_unavailable') ? ' Performance unavailable.' : ''}`;
}

/**
 * Display labels for the ad platform(s) `select_brief_concept_copy_revision`
 * can name in a `platform_not_supported` block. LinkedIn flipped to
 * `ad_copy_platforms.supports_copy_revision = true` 2026-09-24 (Lane R3-1),
 * so this code no longer fires for it — only Google RSA remains. The entry
 * stays (harmless, unused) rather than being pulled the moment its platform
 * flips, matching this map's own job of naming whichever platform the RPC
 * refuses next. A local map rather than importing `platform-limits.ts`'s own
 * (unexported) label table: that file's labels serve character-limit copy,
 * this is one sentence naming why a write refused, and the two do not need
 * to share a seam for two entries.
 */
const UNSUPPORTED_PLATFORM_LABELS: Record<string, string> = {
  linkedin: 'LinkedIn',
  google_rsa: 'Google RSA',
};

/**
 * One sentence per blocked code — never a generic "something went wrong". Each
 * of these is a real, distinct product state named by
 * `select_brief_concept_copy_revision` itself; see
 * `supabase/migrations/20260826220000_ad_copy_platform_registry.sql`.
 */
function blockedSelectionMessage(code: ConceptCopySelectionBlockedCode, platform?: string): string {
  switch (code) {
    case 'version_conflict':
      return 'This concept changed elsewhere since it loaded. Its latest details just reloaded — review them and try selecting again.';
    case 'current_pointer_conflict':
      return 'Someone selected different copy for this concept just now. The current option just refreshed — try again.';
    case 'command_context_missing':
      return 'That copy option no longer matches this concept or brief, so it cannot be made current. Your options just refreshed.';
    case 'revision_not_selectable':
      return 'That option was rejected or already exported, so it cannot be made current. Pick a different option.';
    case 'platform_not_supported':
      return `Copy selection is not wired up for ${platform ? (UNSUPPORTED_PLATFORM_LABELS[platform] ?? platform) : 'this platform'} yet.`;
    default:
      return 'That selection did not save.';
  }
}

function byCreationThenId(a: ConceptCopyVariant, b: ConceptCopyVariant): number {
  if (a.createdAt !== b.createdAt) return a.createdAt < b.createdAt ? -1 : 1;
  return a.id < b.id ? -1 : 1;
}

export function ConceptCopySection({
  conceptId,
  briefId,
  selectedId: controlledSelectedId,
  onSelectIdChange,
  className,
}: ConceptCopySectionProps): JSX.Element {
  const {
    variants,
    isLoading,
    error,
    generate,
    isGenerating,
    moreLikeThis,
    cardData,
    limitBreaches,
    refetch,
  } = useConceptCopyVariants(conceptId);

  // The persisted "current copy" pointer — `project_tasks.current_ad_unit_id`.
  // No-op (never called) whenever `briefId` is absent, so isolated mounts
  // (style guide, tests, any render site without a Brief in scope) keep the
  // section's original local-only behaviour exactly.
  const { currentAdUnitId, selectConceptCopy } = useSelectConceptCopy(conceptId, briefId);

  const [uncontrolledSelectedId, setUncontrolledSelectedId] = useState<string | null>(null);
  const isControlled = controlledSelectedId !== undefined;
  const selectedId = isControlled ? controlledSelectedId : uncontrolledSelectedId;
  const setSelectedId = (id: string): void => {
    if (!isControlled) setUncontrolledSelectedId(id);
    onSelectIdChange?.(id);
  };
  const [notice, setNotice] = useState<Notice | null>(null);
  const commandIds = useRef(new Map<string, string>());
  const retryTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  useEffect(
    () => () => {
      for (const timer of retryTimers.current.values()) clearTimeout(timer);
      retryTimers.current.clear();
    },
    [],
  );

  const ordered = useMemo(() => [...variants].sort(byCreationThenId), [variants]);
  // Precedence: an explicit click this session > the persisted current copy >
  // the oldest option. Before this, an unset `selectedId` fell straight to
  // `ordered[0]` — creation order, not the copy that is actually shipping —
  // so a reload could display an option that was never the current one.
  const selected =
    ordered.find((variant) => variant.id === selectedId) ??
    ordered.find((variant) => variant.id === currentAdUnitId) ??
    ordered[0] ??
    null;
  const selectedEvidenceReceipt = selected ? evidenceReceipt(selected.briefEvidenceLineage) : null;

  const anchorNumber = useMemo(() => {
    if (!selected?.anchoredOnVariantId) return null;
    const index = ordered.findIndex((variant) => variant.id === selected.anchoredOnVariantId);
    return index === -1 ? null : index + 1;
  }, [ordered, selected]);

  /**
   * Fires from every explicit selection — a radio click or "More like this"
   * landing on its fresh sibling — and is the ONLY path that persists
   * `current_ad_unit_id`. The local pill highlight moves immediately
   * (optimistic, matching the pre-existing click feel); a `blocked` response
   * reverts it to the option that was actually current, so the section never
   * keeps showing a pick the server refused as chosen. `briefId` absent means
   * there is no Brief this candidate can be checked against, so the RPC is
   * simply never called — the section still works, display-only, exactly as
   * it did before this write existed.
   */
  const handleSelect = async (variant: ConceptCopyVariant): Promise<void> => {
    const previousId = selected?.id ?? null;
    setSelectedId(variant.id);
    if (variant.id === previousId || !briefId) return;
    setNotice(null);
    try {
      const result = await selectConceptCopy(variant.id);
      if (result.kind === 'blocked') {
        setNotice({
          text: blockedSelectionMessage(result.code, result.platform),
          tone: 'destructive',
        });
        if (previousId) setSelectedId(previousId);
      }
    } catch (cause) {
      setNotice({
        text: cause instanceof Error ? cause.message : 'That selection did not save.',
        tone: 'destructive',
      });
      if (previousId) setSelectedId(previousId);
    }
  };

  const run = async (anchorId?: string): Promise<void> => {
    setNotice(null);
    const commandKey = anchorId ? `anchor:${anchorId}` : 'fresh';
    const currentTimer = retryTimers.current.get(commandKey);
    if (currentTimer) {
      clearTimeout(currentTimer);
      retryTimers.current.delete(commandKey);
    }
    const commandId = commandIds.current.get(commandKey) ?? crypto.randomUUID();
    commandIds.current.set(commandKey, commandId);
    try {
      const result = anchorId
        ? await moreLikeThis(anchorId, SIBLING_COUNT, commandId)
        : await generate({ commandId });

      if ('state' in result && result.state === 'pending') {
        const retryAfterSeconds = Math.max(1, Math.ceil(result.retryAfterSeconds));
        setNotice({
          text: `Mora is still writing these options. Checking again in ${retryAfterSeconds} ${retryAfterSeconds === 1 ? 'second' : 'seconds'}.`,
          tone: 'muted',
        });
        const timer = setTimeout(() => {
          retryTimers.current.delete(commandKey);
          void run(anchorId);
        }, retryAfterSeconds * 1_000);
        retryTimers.current.set(commandKey, timer);
        return;
      }

      if ('state' in result && result.state === 'refused') {
        // A refusal wrote nothing. It is a real, actionable product state — the
        // account has nothing to ground copy in — not an error to swallow.
        setNotice({ text: result.reason, tone: 'muted' });
        commandIds.current.delete(commandKey);
        return;
      }

      commandIds.current.delete(commandKey);

      // Only "More like this" moves the selection: the user asked for a
      // specific new thing and expects to land on it. A fresh unanchored set has
      // no such request, and jumping to whichever row the insert returned first
      // showed `Option 2` selected on a section that had just been empty.
      const fresh = result.variants[0];
      if (anchorId && fresh) void handleSelect(fresh);
      if (result.discardedEchoes > 0) {
        setNotice({
          text:
            result.discardedEchoes === 1
              ? 'One option came back as a repeat of the one you anchored on and was discarded.'
              : `${result.discardedEchoes} options came back as repeats of the one you anchored on and were discarded.`,
          tone: 'muted',
        });
      }
    } catch (cause) {
      setNotice({
        text: cause instanceof Error ? cause.message : 'Copy generation failed.',
        tone: 'destructive',
      });
    }
  };

  return (
    <section
      id="concept-content-section"
      className={cn('flex flex-col gap-2 border-b border-border/40 px-2.5 py-2.5', className)}
    >
      <header className="flex items-center justify-between gap-2">
        <span className={SECTION_LABEL}>Content</span>
        {ordered.length > 0 ? (
          <AiActionButton
            title="Write more copy options"
            busy={isGenerating}
            onClick={() => void run()}
          >
            More options
          </AiActionButton>
        ) : null}
      </header>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      ) : ordered.length === 0 ? (
        /* A full `Empty` block here out-shouted every real control in the
           column — an absent sub-section is not a page-level empty dataset. One
           muted line states the fact, and the action sits next to it at the same
           weight as the canvas's own "Generate this size". */
        <div className="flex flex-col items-start gap-2">
          <p className="text-detail text-muted-foreground">
            No copy for this concept yet. Mora writes options from the brief and what&apos;s on file
            for your brand.
          </p>
          <AiActionButton busy={isGenerating} onClick={() => void run()}>
            {isGenerating ? 'Writing options…' : 'Generate copy'}
          </AiActionButton>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {ordered.length > 1 ? (
            <div role="radiogroup" aria-label="Copy options" className="flex flex-wrap gap-1.5">
              {ordered.map((variant, index) => {
                // The dot says this option truncates SOMEWHERE without making
                // the user open all three — the same vocabulary the size strip
                // uses for a variant that needs review.
                const truncates = COPY_FIELDS.some(
                  (field) => limitBreaches(variant, field).length > 0,
                );
                const isSelected = variant.id === selected?.id;
                return (
                  <button
                    key={variant.id}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    aria-label={`Option ${index + 1}${truncates ? ', truncates on a platform' : ''}`}
                    onClick={() => void handleSelect(variant)}
                    className={cn(
                      SELECTABLE_PILL,
                      isSelected ? SELECTABLE_PILL_ON : SELECTABLE_PILL_OFF,
                      'gap-1 px-2.5 py-1 tabular-nums',
                    )}
                  >
                    {index + 1}
                    {truncates ? (
                      <span aria-hidden className="size-1.5 rounded-full bg-destructive" />
                    ) : null}
                  </button>
                );
              })}
            </div>
          ) : null}

          {anchorNumber ? (
            <p className="text-micro text-muted-foreground">Written from option {anchorNumber}.</p>
          ) : null}

          {selectedEvidenceReceipt ? <p className={ITEM_META}>{selectedEvidenceReceipt}</p> : null}

          {/* THE REVIEW HALF. Copy has been readable here since the section
              landed and un-shippable without leaving for the form page: a
              marketer could see the headline, ask for two more, and then had to
              navigate away to say yes. `status` was already on the read model
              and rendered nowhere. Approve/Reject act on the SELECTED option
              only, which is what `transition_ad_unit_review` accepts. */}
          {selected && briefId ? (
            <AdUnitReviewControls
              briefId={briefId}
              adUnitId={selected.id}
              status={selected.status}
              onReviewed={() => void refetch()}
              onError={(text) => setNotice({ text, tone: 'destructive' })}
            />
          ) : null}

          {selected
            ? COPY_FIELDS.map((field) => {
                const data = cardData(selected, field);
                if (!data) return null;
                return (
                  <CopyVariantCard
                    key={field}
                    {...data}
                    readOnly
                    moreLikeThisDisabled={isGenerating}
                    onMoreLikeThis={() => void run(selected.id)}
                  />
                );
              })
            : null}
        </div>
      )}

      {error ? (
        <p className="text-detail text-destructive">
          {error instanceof Error ? error.message : 'Copy could not be loaded.'}
        </p>
      ) : null}
      {notice ? (
        <p
          className={cn(
            'text-detail',
            notice.tone === 'destructive' ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {notice.text}
        </p>
      ) : null}
    </section>
  );
}
